import { Dispatch, SetStateAction } from 'react';

import { Course, CourseSeasonMovie, Quiz } from '../../../models/CourseModels';
import { SchoolLiveClasses } from '../../../models/SchoolModels';

/**
 * Decide se a url deve tocar no player HLS (Bunny, Cloudflare, mp4 em CDN) em vez do player
 * do Vimeo. O provedor é deduzido da própria url, então migrar uma aula é só trocar o valor
 * do campo `url` no banco - sem coluna nova e sem alteração no backend, que monta a resposta
 * campo a campo e não repassaria uma coluna nova.
 *
 * O Vimeo é o padrão: só sai dele quando a url existe, é http(s) e aponta para um host que
 * não é do Vimeo. Amostragem de 64 temporadas em produção não achou um único `url` fora de
 * `vimeo.com`, então nenhuma aula existente muda de player. Hosts do Vimeo continuam no
 * player do Vimeo mesmo terminando em `.m3u8` (é o caso do HLS legado em
 * `player.vimeo.com/external/*.m3u8`).
 */
export const usesNativePlayer = (url?: string): boolean => {
  const value = url?.trim();

  if (!value || !/^https?:\/\//i.test(value)) {
    return false;
  }

  try {
    const { hostname } = new URL(value);
    const host = hostname.toLowerCase();

    return host !== 'vimeo.com' && !host.endsWith('.vimeo.com');
  } catch {
    return false;
  }
};

export interface CourseVideoPlayerProps {
  actualTime?: {
    loaded: number;
    loadedSeconds: number;
    played: number;
    playedSeconds: number;
  };
  isLoading?: boolean;
  isPlaying?: boolean;
  large?: boolean;
  quizzes: Array<Quiz>;
  courseDetails?: Course | undefined;
  url?: string;
  video?: CourseSeasonMovie | undefined;
  onFinish?(info: any): void;
  onPause?(info: any): void;
  setActualTime?:
    | Dispatch<
        SetStateAction<{
          playedSeconds: number;
          played: number;
          loadedSeconds: number;
          loaded: number;
        }>
      >
    | false;
  setIsPlaying?: Dispatch<SetStateAction<boolean>>;
}

export interface LiveClassVideoPlayerProps {
  url?: string;
  large?: boolean;
  width?: string;
  height?: string;
  video?: SchoolLiveClasses;
  onPause?(info: any): void;
  onFinish?(info: any): void;
}

/**
 * Segundo em que a aula deve retomar: progresso em memória tem precedência sobre o que
 * veio do backend, e aula concluída recomeça do zero.
 */
export const getResumeSeconds = (
  video?: CourseSeasonMovie,
  videos?: Record<string, string>,
): number => {
  if (!video) {
    return 0;
  }

  if (
    video.courseseasonmovieuser &&
    video.courseseasonmovieuser.videostatus === 'completed'
  ) {
    return 0;
  }

  let timeWatched = '00:00:00';

  if (videos && videos[video.url]) {
    timeWatched = videos[video.url];
  } else if (video.courseseasonmovieuser && video.courseseasonmovieuser.videowatched) {
    timeWatched = video.courseseasonmovieuser.videowatched;
  }

  const [hours, minutes, seconds] = timeWatched.split(':');

  return Number(hours) * 60 * 60 + Number(minutes) * 60 + Number(seconds);
};
